/*
 * Presentation Templates — inserting selected slides into the deck and
 * downloading a presentation (or some of its slides) as a standalone .pptx.
 * Inserting: turns a set of selected slide ids into slides in
 * the deck. Thin wrapper around shared/services/pptxTemplateSlideInsert.ts's
 * `insertTemplateSlides` (source-file grouping, sub-deck building, insert
 * loop — identical to Slide Templates' own need, see that module's header)
 * with this feature's own messages/telemetry reasons.
 *
 * Also owns the confirmation copy shown once slides land (BRD 8.9): it names
 * both the count and the source presentation.
 */

import {
  DeckReadOnlyError,
  SelectionUnavailableError,
  buildSubDeckBytes,
  
  fetchSourceBuffer,
  insertTemplateSlides,
  isDeckReadOnly,
  type InsertFailureKind,
  type InsertNoticeCopy,
} from "../../shared/services/pptxTemplateSlideInsert";
import {
  logPresentationTemplatesError,
  type PresentationTemplate,
  type PresentationTemplateSlide,
} from "./presentationTemplatesApi";

export const NO_INSERTABLE_SELECTION_MESSAGE = "None of the selected slides have insertable content yet.";
const SOURCE_DOWNLOAD_FAILED_MESSAGE = "Downloading this slide's source file failed.";

/**
 * Inserts the selected slides, in the order `slides` lists them, immediately
 * after the slide currently selected in the deck (BRD 8.9).
 *
 * @returns how many slides were inserted.
 */
export async function insertPresentationTemplates(
  slides: readonly PresentationTemplateSlide[],
  selectedIds: readonly string[],
): Promise<number> {
  return insertTemplateSlides(
    slides,
    selectedIds,
    { noInsertableSelection: NO_INSERTABLE_SELECTION_MESSAGE, sourceDownloadFailed: SOURCE_DOWNLOAD_FAILED_MESSAGE },
    { log: logPresentationTemplatesError, reasonPrefix: "presentation-templates" },
    { insertAfterCurrent: true },
  );
}

/** Names of the presentations the given slide ids belong to, in manifest order, de-duplicated. */
export function sourcePresentationNames(
  presentations: readonly PresentationTemplate[],
  slideIds: readonly string[],
): string[] {
  const wanted = new Set(slideIds);
  return presentations
    .filter((presentation) => presentation.slides.some((slide) => wanted.has(slide.id)))
    .map((presentation) => presentation.name);
}

/**
 * The confirmation after a successful insert (BRD 8.9):
 * "1 slide from [Name] added after your current slide" /
 * "[N] slides from [Name] added after your current slide". A selection that
 * spans several presentations (selection is flat across them) has no single
 * name to give, so it falls back to a count of presentations.
 */
export function presentationSlidesAddedMessage(count: number, sourceNames: readonly string[]): string {
  const slides = count === 1 ? "1 slide" : `${count} slides`;
  let source = "the library";
  if (sourceNames.length === 1) source = sourceNames[0]!;
  else if (sourceNames.length > 1) source = `${sourceNames.length} presentations`;
  return `${slides} from ${source} added after your current slide`;
}

/* ── Insert failures (BRD §10) ───────────────────────────────────── */

export type { InsertFailureKind };

/** The task-pane notice for each failure that has its own. The first two are
 *  the same wording Slide Templates uses; only "removed" names this feature. */
export const PRESENTATION_INSERT_NOTICE_COPY: Readonly<Record<InsertFailureKind, InsertNoticeCopy>> = {
  readonly: {
    title: "This presentation can't be edited.",
    message: "It's read-only, so nothing was added. Make it editable in PowerPoint, then try again.",
  },
  connection: {
    title: "Connection lost",
    message: "Nothing was added to your presentation. Check your connection, then try again.",
  },
  removed: {
    title: "That presentation template is no longer available",
    message: "Your organisation updated the library, so nothing was inserted. Choose another one instead.",
  },
};

/** Thrown when a selected slide's presentation is no longer in the library. */
export class PresentationSelectionUnavailableError extends SelectionUnavailableError {
  constructor() {
    super(PRESENTATION_INSERT_NOTICE_COPY.removed.message);
  }
}

/** Thrown before an insert is attempted on a read-only presentation. */
export class PresentationDeckReadOnlyError extends DeckReadOnlyError {
  constructor() {
    super(PRESENTATION_INSERT_NOTICE_COPY.readonly.message);
  }
}

/** True when PowerPoint reports the open presentation as read-only. */
export function isPresentationDeckReadOnly(): boolean {
  return isDeckReadOnly(logPresentationTemplatesError, "presentation-templates-read-only-check");
}



/* ── Download (BRD 8.4, 8.7) ─────────────────────────────────────── */

export const NO_DOWNLOADABLE_SOURCE_MESSAGE = "This presentation has no downloadable file yet.";
export const NO_DOWNLOADABLE_SLIDES_MESSAGE = "None of the selected slides can be downloaded yet.";
const PACK_DOWNLOAD_FAILED_MESSAGE = "Downloading this presentation failed.";
const PPTX_MIME = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const FALLBACK_FILE_NAME = "Presentation";

/** Strips characters Windows/macOS reject in a filename, and any `.pptx` the
 *  name already carries so it isn't doubled. `suffix` (e.g. "3 slides") is
 *  appended in brackets before the extension. */
export function presentationPackFileName(name: string, suffix?: string): string {
  const base = name
    .replace(/\.pptx$/i, "")
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const bracketed = suffix ? ` (${suffix})` : "";
  return `${base || FALLBACK_FILE_NAME}${bracketed}.pptx`;
}

function saveBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.style.display = "none";
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Revoke on the next tick — revoking synchronously can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

/**
 * Downloads the presentation as `{Name}.pptx`, or — when `slideIds` is given —
 * only those of its slides, in the deck's own order, as `{Name} (N slides).pptx`.
 * Throws a user-facing error when it can't.
 */
export async function downloadPresentationPack(
  presentation: PresentationTemplate,
  slideIds?: readonly string[],
): Promise<void> {
  const sourceFileUrl = presentation.slides.find((slide) => slide.sourceFileUrl)?.sourceFileUrl ?? null;
  if (!sourceFileUrl) throw new Error(NO_DOWNLOADABLE_SOURCE_MESSAGE);

  // Resolved before any network work, so an unusable selection fails fast.
  let positions: number[] | null = null;
  if (slideIds) {
    const wanted = new Set(slideIds);
    positions = presentation.slides
      .filter((slide) => wanted.has(slide.id) && slide.slideIndex !== null)
      // slideIndex is 1-based; buildSubDeckBytes wants 0-based positions.
      .map((slide) => slide.slideIndex! - 1);
    if (positions.length === 0) throw new Error(NO_DOWNLOADABLE_SLIDES_MESSAGE);
  }

  const buffer = await fetchSourceBuffer(
    sourceFileUrl,
    { noInsertableSelection: NO_DOWNLOADABLE_SOURCE_MESSAGE, sourceDownloadFailed: PACK_DOWNLOAD_FAILED_MESSAGE },
    { log: logPresentationTemplatesError, reasonPrefix: "presentation-templates-download" },
  );

  if (positions) {
    const label = positions.length === 1 ? "1 slide" : `${positions.length} slides`;
    // Copied into a plain ArrayBuffer-backed array: zip output is typed ArrayBufferLike, which Blob rejects.
    const bytes = new Uint8Array(buildSubDeckBytes(buffer, positions));
    saveBlob(new Blob([bytes], { type: PPTX_MIME }), presentationPackFileName(presentation.name, label));
    return;
  }
  saveBlob(new Blob([buffer], { type: PPTX_MIME }), presentationPackFileName(presentation.name));
}

export {classifyInsertFailure} from "../../shared/services/pptxTemplateSlideInsert";